import {useEffect, useRef} from "react";
import appConfig from "../config";

export type ChatRequestEvent = {
    kind: 'request';
    channel_idt: number;
    to_usr_idt: number;
    from_usr_idt: number;
    at: number;
};

export type ChatResponseEvent = {
    kind: 'response';
    channel_idt: number;
    to_usr_idt: number;
    from_usr_idt: number;
    state: 'ACCEPTED' | 'REFUSED' | 'BLOCKED';
    at: number;
};

export type ChatMessageEvent = {
    kind: 'message';
    channel_idt: number;
    to_usr_idt: number;
    from_usr_idt: number;
    message_idt: number;
    at: number;
};

export type ChatEvent = ChatRequestEvent | ChatResponseEvent | ChatMessageEvent;

type Options = {
    onRequest?: (evt: ChatRequestEvent) => void;
    onResponse?: (evt: ChatResponseEvent) => void;
    onMessage?: (evt: ChatMessageEvent) => void;
};

// Opens an EventSource against /chat/events?token=<jwt> for the connected
// user. The backend uses the JWT to filter so the client only receives
// events relevant to it. Auto-closes on logout / token change.
export function useChatEvents(token: string | null | undefined, opts: Options = {}) {
    const handlers = useRef(opts);
    handlers.current = opts;

    useEffect(() => {
        if (!token) return;
        if (typeof EventSource === 'undefined') return;

        const url = `${appConfig.apiURL}/chat/events?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);

        const dispatch = (e: MessageEvent) => {
            try {
                const evt = JSON.parse(e.data) as ChatEvent;
                if (evt.kind === 'request') handlers.current.onRequest?.(evt);
                else if (evt.kind === 'response') handlers.current.onResponse?.(evt);
                else if (evt.kind === 'message') handlers.current.onMessage?.(evt);
            } catch (err) {
                console.error('useChatEvents: bad payload', err);
            }
        };

        es.addEventListener('request', dispatch);
        es.addEventListener('response', dispatch);
        es.addEventListener('message', dispatch);

        es.onerror = (err) => {
            console.warn('useChatEvents: stream error', err);
        };

        return () => es.close();
    }, [token]);
}
